import { useMutation, UseMutationResult, useQuery, UseQueryResult } from 'react-query';
import type { IConversationMessage } from '../algebra/conversations';
import type { ResponseFormat } from '../algebra/global';
import type { IQueuePosition } from '../algebra/waiting-room';

import { Http } from '../prelude/http';
import { SurvivorsConversationStore } from './useGetSurvivorsConversation';

export const fetchQueuePosition = async (messaging_service_key: string, task_sid: string, captchaToken: Record<string, string>): Promise<IQueuePosition> =>
  Http.AwsWafIntegrationRequest<ResponseFormat<IQueuePosition>>(
    'GET',
    `/hotlines/api/v1/ingress/webchats.status?messaging_service_key=${messaging_service_key}&task_sid=${task_sid}`,
    captchaToken
  ).then((res) => res.data);

export default function useGetQueuePosition(
  messaging_service_key: string,
  task_sid: string,
  captchaToken: Record<string, string>,
  captchaFlag: string
): UseQueryResult<IQueuePosition, { errors: string[] }> {
  return useQuery(['queuePosition', task_sid], () => fetchQueuePosition(messaging_service_key, task_sid, captchaToken), {
    enabled: messaging_service_key !== '' && task_sid !== '' && captchaFlag !== 'true',
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
  });
}

//delay operation API
interface IDelayHotlineRequest {
  messaging_service_key: string;
  task_sid: string;
  captchaToken: Record<string, string>;
}
export function useDelayOperation(): UseMutationResult<string, unknown, IDelayHotlineRequest, unknown> {
  return useMutation(
    async ({ messaging_service_key, task_sid, captchaToken }) =>
      Http.Request<ResponseFormat<string>>('POST', `/hotlines/api/v1/ingress/webchats.update?messaging_service_key=${messaging_service_key}&task_sid=${task_sid}&response=delay`, captchaToken).then(
        (res) => res.data
      ),
    {
      onSuccess: () => SurvivorsConversationStore.deferHotlines(),
    }
  );
}

export function useEndChatOperation(): UseMutationResult<string, unknown, IDelayHotlineRequest, unknown> {
  return useMutation(
    async ({ messaging_service_key, task_sid, captchaToken }) =>
      Http.Request<ResponseFormat<string>>('POST', `/hotlines/api/v1/ingress/webchats.complete?messaging_service_key=${messaging_service_key}&task_sid=${task_sid}`, captchaToken).then(
        (res) => res.data
      ),
    {
      onSuccess: () => {
        SurvivorsConversationStore.endConversation();
      },
    }
  );
}

interface IConversationResponse {
  conversation_id: string;
  conversations: IConversationMessage[];
}

// getting hotlines messages
export const useSurvivorsMessages = (task_sid: string, loadMessages: boolean): UseQueryResult<IConversationResponse> => {
  return useQuery(
    ['survivors_messages', task_sid],
    () => Http.Request<ResponseFormat<IConversationResponse>>('GET', `/hotlines/api/v1/conversations.get?task_sid=${task_sid}`).then((res) => res.data),
    {
      enabled: task_sid !== null && task_sid !== undefined && loadMessages,
      staleTime: Infinity,
      cacheTime: 0,
    }
  );
};
